import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  expandedGroups: { [key: string]: boolean };
  toggleGroup: (groupId: string) => void;
  setGroupExpanded: (groupId: string, expanded: boolean) => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
  isMinimized: boolean;
  toggleMinimize: () => void;
  setMinimized: (minimized: boolean) => void;
  isMobile: boolean;
  setIsMobile: (mobile: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    return window.innerWidth < 768; // md breakpoint
  });

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    // Mobile'da varsayılan olarak kapalı, desktop'ta açık
    const saved = localStorage.getItem('sidebar-open');
    if (saved === null) {
      const isMobileDevice = window.innerWidth < 768;
      const defaultOpen = !isMobileDevice;
      localStorage.setItem('sidebar-open', JSON.stringify(defaultOpen));
      return defaultOpen;
    }
    return JSON.parse(saved);
  });

  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>(() => {
    // localStorage'dan kayıtlı grup durumlarını oku
    const saved = localStorage.getItem('sidebar-expanded-groups');
    if (saved) {
      return JSON.parse(saved);
    }
    // Varsayılan değerler (eğer localStorage'da yoksa)
    return {
      'talep-yonetimi': true,
      'teklif-yonetimi': true,
      'tedarikci-ekranlari': true,
      'siparis-yonetimi': true,
      'rapor-surecleri': true,
      'urun-yonetimi': true
    };
  });

  // expandedGroups değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebar-expanded-groups', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    // localStorage'dan sidebar genişliğini oku
    const saved = localStorage.getItem('sidebar-width');
    return saved ? JSON.parse(saved) : 256; // Default 256px (w-64)
  });

  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(() => {
    // localStorage'dan minimized durumunu oku
    const saved = localStorage.getItem('sidebar-minimized');
    return saved ? JSON.parse(saved) : false;
  });

  // Sidebar durumu değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebar-open', JSON.stringify(isOpen));
  }, [isOpen]);

  // Minimized durumu değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebar-minimized', JSON.stringify(isMinimized));
  }, [isMinimized]);

  // Sidebar genişliği değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebar-width', JSON.stringify(sidebarWidth));
  }, [sidebarWidth]);

  // Window resize listener for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Mobile'da sidebar'ı kapat, desktop'ta açık tut
      if (mobile && isOpen) {
        setIsOpen(false);
      } else if (!mobile && !isOpen) {
        // Desktop'a geçişte sidebar'ı açık tut
        setIsOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(prev => !prev);
  };

  const setSidebarOpen = (open: boolean) => {
    setIsOpen(open);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const setGroupExpanded = (groupId: string, expanded: boolean) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: expanded
    }));
  };

  const toggleMinimize = () => {
    setIsMinimized(prev => !prev);
  };

  const setMinimized = (minimized: boolean) => {
    setIsMinimized(minimized);
  };

  return (
    <SidebarContext.Provider value={{ 
      isOpen, 
      toggleSidebar, 
      setSidebarOpen, 
      expandedGroups, 
      toggleGroup, 
      setGroupExpanded,
      sidebarWidth,
      setSidebarWidth,
      isResizing,
      setIsResizing,
      isMinimized,
      toggleMinimize,
      setMinimized,
      isMobile,
      setIsMobile
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
